import { useEffect } from 'react'
import { Outlet } from 'react-router-dom'
import { GlobalPlanDialog } from '@/components/billing/GlobalPlanDialog'
import { GlobalErrorListener } from '@/components/layout/GlobalErrorListener'
import { ToastViewport } from '@/components/ui/Toast'
import { useAppConfigStore } from '@/stores/appConfigStore'
import { useSettingsStore } from '@/stores/settingsStore'

export function RootLayout() {
  const themePreference = useSettingsStore((s) => s.themePreference)
  const setResolvedTheme = useSettingsStore((s) => s.setResolvedTheme)
  const loadAppConfig = useAppConfigStore((s) => s.load)

  // Runtime feature flags (billing on/off) — fetched once, before any billing UI renders.
  useEffect(() => {
    void loadAppConfig()
  }, [loadAppConfig])

  // Apply theme to <html> on every route — App.tsx only mounts inside EditorPage,
  // so this effect must live here to cover workspace/project list pages too.
  useEffect(() => {
    const root = document.documentElement

    const apply = (resolved: 'light' | 'dark') => {
      root.setAttribute('data-theme', resolved)
      root.classList.toggle('dark', resolved === 'dark')
      setResolvedTheme(resolved)
    }

    if (themePreference === 'dark') { apply('dark'); return }
    if (themePreference === 'light') { apply('light'); return }

    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const sync = () => apply(mq.matches ? 'dark' : 'light')
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [themePreference, setResolvedTheme])

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-app)' }}>
      <GlobalErrorListener />
      <GlobalPlanDialog />
      <Outlet />
      <ToastViewport />
    </div>
  )
}
